/**
 * CR282: Resolved Fields Debug Panel
 * 
 * Editor-only collapsible panel that shows the actual resolved values
 * the UI is using for rendering. Helps designers verify field bindings
 * are working correctly.
 */

import { useState } from "react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, ChevronRight, CheckCircle2, XCircle, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import type { FieldDebug, FieldCategory } from "@/lib/deals/fieldResolver";

interface ResolvedFieldsDebugPanelProps {
  fieldMap: FieldDebug[];
  className?: string;
}

const CATEGORY_LABELS: Record<FieldCategory, string> = {
  header: "Header",
  value: "Value Fields",
  dates: "Dates",
  urls: "URLs",
  fine_print: "Fine Print",
  brands: "Brands",
  source_meta: "Source Metadata",
};

const CATEGORY_ORDER: FieldCategory[] = [
  "value",
  "dates", 
  "header",
  "brands",
  "urls",
  "fine_print",
  "source_meta",
];

function StatusIcon({ status }: { status: FieldDebug["status"] }) {
  switch (status) {
    case "present":
      return <CheckCircle2 className="h-3 w-3 text-green-600" />;
    case "null":
      return <AlertCircle className="h-3 w-3 text-amber-500" />;
    case "missing":
      return <XCircle className="h-3 w-3 text-red-400" />;
  }
}

function formatValue(value: unknown): string {
  if (value === null) return "null";
  if (value === undefined) return "undefined";
  if (typeof value === "number") return value.toString();
  if (typeof value === "string") {
    if (value.length > 60) return value.slice(0, 60) + "…";
    return value;
  }
  if (typeof value === "object") return JSON.stringify(value).slice(0, 60);
  return String(value);
}

export function ResolvedFieldsDebugPanel({ fieldMap, className }: ResolvedFieldsDebugPanelProps) {
  const [isOpen, setIsOpen] = useState(false);
  
  // Group fields by category
  const grouped = CATEGORY_ORDER.reduce((acc, cat) => {
    acc[cat] = fieldMap.filter(f => f.category === cat);
    return acc;
  }, {} as Record<FieldCategory, FieldDebug[]>);
  
  // Calculate summary stats
  const presentCount = fieldMap.filter(f => f.status === "present").length;
  const missingCount = fieldMap.filter(f => f.status === "missing").length;
  
  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className={cn("border rounded-lg bg-background", className)}>
      <CollapsibleTrigger className="w-full flex items-center justify-between p-3 hover:bg-muted/50 transition-colors">
        <div className="flex items-center gap-2">
          {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          <span className="text-sm font-medium">Resolved Fields</span>
          <Badge variant="outline" className="text-[10px]">CR282</Badge>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span className="text-green-600">{presentCount} present</span>
          <span className="text-red-400">{missingCount} missing</span>
        </div>
      </CollapsibleTrigger>
      
      <CollapsibleContent>
        <div className="border-t p-3 space-y-4 max-h-[400px] overflow-y-auto">
          {CATEGORY_ORDER.map(cat => {
            const fields = grouped[cat];
            if (!fields || fields.length === 0) return null;
            
            return (
              <div key={cat}>
                <h4 className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wider">
                  {CATEGORY_LABELS[cat]}
                </h4>
                <div className="space-y-1">
                  {fields.map(field => (
                    <div
                      key={field.key}
                      className={cn(
                        "flex items-start gap-2 p-2 rounded text-xs",
                        field.status === "present" && "bg-green-50/50",
                        field.status === "missing" && "bg-red-50/30",
                        field.status === "null" && "bg-amber-50/30"
                      )}
                    >
                      <StatusIcon status={field.status} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-[11px] font-medium">{field.key}</span>
                          {field.notes && (
                            <span className="text-amber-600 text-[10px]">{field.notes}</span>
                          )}
                        </div>
                        {field.status === "present" && (
                          <div className="mt-0.5 text-muted-foreground truncate">
                            <span className="font-mono">{formatValue(field.value)}</span>
                          </div>
                        )}
                        <div className="mt-0.5 text-[10px] text-muted-foreground/70">
                          via <code className="px-1 py-0.5 bg-muted rounded">{field.sourcePath}</code>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
          
          {/* Quick summary for key BONUS deal fields */}
          <div className="pt-2 border-t">
            <h4 className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wider">
              Quick Check (BONUS Deal Fields)
            </h4>
            <div className="grid grid-cols-2 gap-2 text-xs">
              {["bonus_value_cad", "qualifying_spend_cad", "valid_from", "valid_until", "dates_type", "date_phrase", "computed_value_cad", "effective_percent_back", "points_program_id"].map(key => {
                const field = fieldMap.find(f => f.key === key);
                if (!field) return null;
                return (
                  <div key={key} className="flex items-center gap-1.5">
                    <StatusIcon status={field.status} />
                    <span className="font-mono text-[10px]">{key}:</span>
                    <span className={cn(
                      "font-mono text-[10px]",
                      field.status === "present" ? "text-green-700" : "text-muted-foreground"
                    )}>
                      {field.status === "present" ? formatValue(field.value) : "—"}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
